import React, { useState, useEffect } from "react";
import { useAddQuestionMutation } from "../../redux/api/coachApi";
import { SERVER_ROUTE, DEFAULT_ROUTE } from "../../routes/api/api_routes";

const QuizModal = ({ onClose, onQuestionCreated }) => {
  const [addQuestion] = useAddQuestionMutation();

  // Inputs
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState({ A: "", B: "", C: "", D: "" });
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [technology, setTechnology] = useState("");
  const [category, setCategory] = useState("");
  const [niveau, setNiveau] = useState("");

  // Données récupérées depuis l'API
  const [categories, setCategories] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [loading, setLoading] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("access");

      try {
        // Categories
        const resCat = await fetch(`${DEFAULT_ROUTE}category/`, {
          headers: { Authorization: `JWT ${token}` },
        });
        const catData = await resCat.json();
        setCategories(Array.isArray(catData.data) ? catData.data : []);

        // Technologies
        const resTech = await fetch(`${DEFAULT_ROUTE}technology/`, {
          headers: { Authorization: `JWT ${token}` },
        });
        const techData = await resTech.json();
        setTechnologies(Array.isArray(techData.data) ? techData.data : []);

        // Niveaux
        const resNiv = await fetch(`${DEFAULT_ROUTE}niveau/`, {
          headers: { Authorization: `JWT ${token}` },
        });
        const nivData = await resNiv.json();
        setNiveaux(Array.isArray(nivData.data) ? nivData.data : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
      }
    };

    fetchData();
  }, []);

  // 🔹 Quand on choisit une catégorie => filtrer technologies sans reset brutal
  const handleCategoryChange = (value) => {
    setCategory(value);

    // Vérifier si la technologie choisie est encore valide
    if (
      technology &&
      !technologies.some(
        (t) => t.id === parseInt(technology) && t.category === parseInt(value)
      )
    ) {
      setTechnology(""); // reset seulement si pas compatible
    }
  };

  // 🔹 Quand on choisit une technologie => forcer la catégorie associée
  const handleTechnologyChange = (value) => {
    setTechnology(value);
    const tech = technologies.find((t) => t.id === parseInt(value));
    if (tech?.category) {
      setCategory(tech.category.toString());
    }
  };

  // 🔹 Filtrer les technologies liées à la catégorie choisie
  const filteredTechnologies = category
    ? technologies.filter((t) => t.category === parseInt(category))
    : technologies;

  const handleCheckbox = (letter) => {
    setCorrectAnswers((prev) =>
      prev.includes(letter)
        ? prev.filter((l) => l !== letter)
        : [...prev, letter]
    );
  };

  // 🔹 Validation avant soumission
  const handleSubmit = async () => {
    const newErrors = {};

    if (!category) newErrors.category = "Veuillez choisir une catégorie";
    if (!technology) newErrors.technology = "Veuillez choisir une technologie";
    if (!niveau) newErrors.niveau = "Veuillez choisir un niveau";
    if (!question.trim())
      newErrors.question = "La question ne peut pas être vide";

    // Vérifie que toutes les réponses sont remplies
    Object.entries(answers).forEach(([letter, value]) => {
      if (!value.trim()) newErrors[letter] = `La réponse ${letter} est vide`;
    });

    // Vérifie qu'au moins une bonne réponse est sélectionnée
    if (correctAnswers.length === 0)
      newErrors.correctAnswers = "Sélectionnez au moins une bonne réponse";

    // Si des erreurs, on les affiche et on stoppe le submit
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({}); // pas d'erreurs => on réinitialise

    try {
      const token = localStorage.getItem("access");
      setLoading(true);

      const questionData = {
        html: question,
        text: "Aucune explication disponible",
        explanation: "",
        is_published: true,
        maximum_marks: 4,
        categorie_id: category,
        technology_id: technology,
        niveau_id: niveau,
      };

      const createdQuestion = await addQuestion(questionData).unwrap();

      const choices = Object.entries(answers).map(([letter, value]) => ({
        html: value,
        is_correct: correctAnswers.includes(letter),
        question: createdQuestion.id,
      }));

      await Promise.all(
        choices.map((choice) =>
          fetch(`${DEFAULT_ROUTE}choice/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `JWT ${token}`,
            },
            body: JSON.stringify(choice),
          })
        )
      );

      if (onQuestionCreated) onQuestionCreated(createdQuestion);
      onClose();
    } catch (error) {
      console.error("Erreur lors de la création de la question :", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40  backdrop-blur-sm flex justify-center items-center px-4">
      <div className=" bg-white rounded-xl mx-auto p-6 w-full max-w-4xl shadow-lg">
        <h2 className="text-xl font-semibold mb-4">Créer une Question</h2>

        <div className="flex justify-between mb-4">
          {/* Sélection Catégorie */}
          <div>
            <select
              disabled={loading}
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="border p-2 rounded mb-1 w-[200px]"
            >
              <option value="">Choisir la catégorie</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-600 text-sm">{errors.category}</p>
            )}
          </div>

          {/* Sélection Niveau */}
          <div>
            <select
              disabled={loading}
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
              className="border p-2 rounded mb-1 w-[200px]"
            >
              <option value="">Choisir le Niveau</option>
              {niveaux.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                </option>
              ))}
            </select>
            {errors.niveau && (
              <p className="text-red-600 text-sm">{errors.niveau}</p>
            )}
          </div>

          {/* Sélection Technologie */}
          <div>
            <select
              disabled={loading}
              value={technology}
              onChange={(e) => handleTechnologyChange(e.target.value)}
              className="border p-2 rounded mb-1 w-[200px]"
            >
              <option value="">Choisir la technologie</option>
              {filteredTechnologies.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {errors.technology && (
              <p className="text-red-600 text-sm">{errors.technology}</p>
            )}
          </div>
        </div>

        {/* Question */}
        <div className="mb-4">
          <input
            disabled={loading}
            type="text"
            placeholder="Saisir la question ici"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="border rounded p-2 w-full"
          />
          {errors.question && (
            <p className="text-red-600 text-sm">{errors.question}</p>
          )}
        </div>

        {/* Réponses */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {["A", "B", "C", "D"].map((letter) => (
            <div key={letter}>
              <input
                type="text"
                placeholder={`${letter})`}
                value={answers[letter]}
                onChange={(e) =>
                  setAnswers({ ...answers, [letter]: e.target.value })
                }
                className="border rounded p-2 w-full"
              />
              {errors[letter] && (
                <p className="text-red-600 text-sm">{errors[letter]}</p>
              )}
            </div>
          ))}
        </div>

        {/* Correct answers */}
        <div className="mb-4">
          <p className="font-medium">Bonne(s) réponse(s) :</p>
          <div className="flex gap-4">
            {["A", "B", "C", "D"].map((letter) => (
              <label key={letter} className="flex items-center gap-2">
                <input
                  disabled={loading}
                  type="checkbox"
                  checked={correctAnswers.includes(letter)}
                  onChange={() => handleCheckbox(letter)}
                />
                {letter})
              </label>
            ))}
          </div>
          {errors.correctAnswers && (
            <p className="text-red-600 text-sm">{errors.correctAnswers}</p>
          )}
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded">
            Annuler
          </button>
          <button
            disabled={loading}
            onClick={handleSubmit}
            className="px-4 py-2 bg-orange-400 text-white rounded hover:bg-orange-500"
          >
            {loading ? "Création..." : "Valider"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizModal;
